import React, { useEffect, useState } from 'react';
import { Table, Spin, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface CategoryData {
  id: string | number;
  code: string;
  name: string;
  total: number;
}

interface DepartmentData {
  id: string | number;
  code: string;
  name: string;
  total: number;
  categories?: CategoryData[];
}

interface ImportExportNominalCountsProps {
  reportKey: string;
  filters: any;
}

const ImportExportNominalCount: React.FC<ImportExportNominalCountsProps> = ({ reportKey, filters }) => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<DepartmentData[]>([]);

  useEffect(() => {
    if (reportKey) fetchReportData();
  }, [reportKey, filters]);

  const fetchReportData = async () => {
    const reportApi = getReportApiByKey(reportKey);
    if (!reportApi) {
      message.error('Invalid report key.');
      return;
    }

    try {
      setLoading(true);
      const response = await reportApi.generateReport(filters);

      // Transform the nested object structure to array
      const departments: DepartmentData[] = Object.entries(response?.report?.departments || {}).map(
        ([deptId, deptData]: [string, any]) => {
          const categories = Object.entries(deptData.categories || {}).map(
            ([categoryId, categoryData]: [string, any]) => ({
              id: categoryId,
              code: categoryData.category_code,
              name: categoryData.category_name,
              total: categoryData.total || 0,
            }),
          );

          return {
            id: deptId,
            code: deptData.department_code,
            name: deptData.department_name,
            total: deptData.total || 0,
            categories: categories,
          };
        },
      );

      setReportData(departments);
    } catch (error) {
      console.error(error);
      message.error('Failed to load report data.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Department / Category',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Count',
      dataIndex: 'total',
      key: 'total',
      align: 'right' as const,
      render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
    },
  ];

  // Transform data for table display with nested structure
  const transformData = (departments: DepartmentData[]) =>
    departments.map((dept) => ({
      key: dept.id,
      name: `${dept.code} - ${dept.name}`,
      total: dept.total,
      children: dept.categories?.map((category) => ({
        key: `${dept.id}-${category.id}`,
        name: `↳ ${category.code} - ${category.name}`,
        total: category.total,
      })),
    }));

  const tableData = transformData(reportData);

  return (
    <Spin spinning={loading}>
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        bordered
        expandable={{
          defaultExpandAllRows: true,
        }}
      />
    </Spin>
  );
};

export default ImportExportNominalCount;
